import { useState } from 'react';
import { Link } from 'react-router';
import { SEO } from '@/components/seo/seo';
import { clsx } from 'clsx';

const guides = [
  {
    title: 'How Fixtyer Works',
    description: 'The full process from start to finish',
    href: '/how-it-works',
    icon: '🔄',
  },
  {
    title: 'Gathering Evidence',
    description: 'What to photograph and how',
    href: '/guides/photographing-pothole-damage',
    icon: '📸',
  },
  {
    title: 'Submitting Your Claim',
    description: 'How to send your pack to the council',
    href: '/guides/claim-timeline',
    icon: '📤',
  },
  {
    title: 'Understanding Responses',
    description: 'What happens after you submit',
    href: '/guides/understanding-rejection-reasons',
    icon: '📬',
  },
];

const faqs = [
  {
    question: 'How much does it cost?',
    answer: 'The Council Claim Pack is £35. Court escalation document preparation is planned, but not currently available to purchase. No subscriptions, no hidden fees.',
  },
  {
    question: 'How long does a claim take?',
    answer: 'It depends on the council. Some respond within weeks, others take months. We prepare your documents — the council sets the pace after that.',
  },
  {
    question: 'Do you submit the claim for me?',
    answer: 'No. We prepare everything you need, but you submit it yourself. You stay in control throughout.',
  },
  {
    question: 'What if my claim is rejected?',
    answer: 'You can appeal or consider small claims court. Mac can help you understand your options, and dedicated court document preparation is planned for a later release.',
  },
  {
    question: 'Is Mac a real person?',
    answer: 'No — Mac is our AI-powered assistant. He helps review your evidence and prepare your documents, but he\'s not a solicitor and doesn\'t give legal advice.',
  },
];

export function SupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEO 
        title="Support | Fixtyer"
        description="Need help with your pothole claim? Browse our guides or get in touch with the Fixtyer team."
        canonical="/support"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-surface-50 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Top diagonal stripe bar */}
          <div 
            className="absolute top-0 left-0 right-0 h-8 z-10"
            style={{
              background: `repeating-linear-gradient(
                -45deg,
                #f97316,
                #f97316 40px,
                #1e40af 40px,
                #1e40af 80px
              )`
            }}
          />
          
          <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-accent-50" />
          
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 sm:pt-24 sm:pb-24 text-center">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 mb-4">
              Need a hand?
            </h1>
            <p className="text-lg sm:text-xl text-surface-600 max-w-2xl mx-auto">
              Whether you're stuck on something or just have a question, we're here to help.
            </p>
          </div>
          
          {/* Bottom diagonal stripe bar */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-8 z-10"
            style={{
              background: `repeating-linear-gradient(
                -45deg,
                #1e40af,
                #1e40af 40px,
                #f97316 40px,
                #f97316 80px
              )`
            }}
          />
        </section>

        {/* Start Here - Guides */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-950 mb-3">
                Start here
              </h2>
              <p className="text-surface-600 max-w-xl mx-auto">
                Most questions are covered in our guides. They'll walk you through everything step by step.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {guides.map((guide) => (
                <Link
                  key={guide.href}
                  to={guide.href}
                  className="group bg-white rounded-xl border border-surface-200 p-5 hover:border-brand-300 hover:shadow-lg transition-all"
                >
                  <div className="text-3xl mb-3">{guide.icon}</div>
                  <h3 className="font-display font-semibold text-brand-950 mb-1 group-hover:text-brand-700 transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-surface-500">{guide.description}</p>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/guides"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 transition-colors"
              >
                Browse All Guides
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Answers - FAQ */}
        <section className="py-16 bg-surface-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-950 mb-3">
                Quick answers
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-surface-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
                  >
                    <span className="font-medium text-brand-950">{faq.question}</span>
                    <span
                      className={clsx(
                        "text-surface-400 transition-transform flex-shrink-0",
                        openFaq === index && "rotate-180"
                      )}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={clsx(
                      "grid transition-all duration-200",
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    )}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-4 text-surface-600">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Still Stuck - Contact */}
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-950 mb-3">
              Still stuck?
            </h2>
            <p className="text-surface-600 mb-8">
              If the guides don't cover it, drop us a message. We'll get back to you as soon as we can.
            </p>

            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-xl font-medium hover:from-brand-700 hover:to-brand-800 transition-all shadow-lg shadow-brand-600/20"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Get in Touch
            </Link>

            <p className="mt-4 text-sm text-surface-500">
              We typically respond within 1-2 working days.
            </p>
          </div>
        </section>

        {/* Disclaimer Footer */}
        <section className="py-8 border-t border-surface-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-surface-400">
              Fixtyer helps you prepare pothole damage claims. We're not solicitors and we don't provide legal advice. 
              For complex legal matters, seek independent legal advice.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
